import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import PulseLoader from 'react-spinners/PulseLoader';

// MaterialUI
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import TextField from '@material-ui/core/TextField';

// app: components
import DisplayNameChooser from 'components/DisplayNameChooser';

// app: constants
import { COMMENT_RULES } from 'constants/index';

// assets
import CssCommentForm from './CommentForm.scss';

const MAX_LENGTH = COMMENT_RULES.question.maxLength;

const QuestionForm = React.forwardRef((props, ref) => {
  const {
    focused,
    isOpen,
    onBlur,
    onChange,
    onClose,
    onFocus,
    hasOwnQuestions,
    onOpen,
    onSubmit,
    optionsMenu,
    question,
    saveInProgress,
    saveError,
    commentId,
  } = props;

  useStyles(CssCommentForm);

  const [isDirty, setDirty] = useState(false);

  const handleQuestionChange = event => {
    const { value } = event.target;
    if (!isOpen) {
      onOpen();
    }
    setDirty(true);
    onChange({ question: value.replace('\n', '') });
  };

  const handleSubmit = event => {
    event.preventDefault();
    const payload = {
      question,
      is_new: true,
    };

    if (commentId) {
      payload.id = commentId;
      payload.is_new = false;
    }

    onSubmit(payload);
  };

  const handleClose = () => {
    onChange({ question: null });
    onClose();
  };

  const handleBlur = event => {
    setDirty(!!question.length);
    onBlur(event);
  };

  const saveButtonText = useMemo(
    () => {
      if (commentId) {
        return 'Update question';
      }

      return 'Ask Question';
    },
    [commentId],
  );

  const questionLabel = useMemo(
    () => {
      if (commentId) {
        return 'Your question';
      }

      if (hasOwnQuestions) {
        return 'Ask another question';
      }

      return 'Your question';
    },
    [commentId, hasOwnQuestions],
  );

  return (
    <form noValidate className="comment-form" onSubmit={handleSubmit}>
      <Collapse in={isOpen} timeout={300}>
        <div className="comment-form__row">
          <DisplayNameChooser />
        </div>
      </Collapse>
      <div className="comment-form__row margin-top-10">
        <TextField
          error={isOpen && isDirty && !question.length}
          focused={focused}
          fullWidth
          helperText={isOpen ? `${question.trim().length}/${MAX_LENGTH} characters` : null}
          id="question"
          label={questionLabel}
          maxLength={MAX_LENGTH}
          multiline
          onBlur={handleBlur}
          onChange={handleQuestionChange}
          onClick={onOpen}
          onFocus={onFocus}
          minRows={1}
          maxRows={isOpen ? 3 : 1}
          inputRef={ref}
          type="text"
          value={question}
          variant="outlined"
        />
      </div>
      <Collapse in={isOpen} timeout={300}>
        {optionsMenu ? <div className="comment-form__dots-menu">{optionsMenu}</div> : null}
        {saveError ? (
          <div className="comment-form__row comment-form__centered margin-top-10">
            <Alert severity="error">{saveError}</Alert>
          </div>
        ) : null}

        <div className="comment-form__row comment-form__buttons">
          <Button
            className="comment-form__button"
            onClick={handleClose}
            size="large"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            className="comment-form__button"
            color="primary"
            disabled={!question.trim().length || saveInProgress}
            size="large"
            type="submit"
            variant="contained"
          >
            <PulseLoader loading={saveInProgress} color="#fff" sizeUnit="px" size={10} />
            {saveInProgress ? '' : saveButtonText}
          </Button>
        </div>
      </Collapse>
    </form>
  );
});

QuestionForm.propTypes = {
  focused: PropTypes.bool,
  hasOwnQuestions: PropTypes.bool,
  question: PropTypes.string,
  commentId: PropTypes.string,
  isOpen: PropTypes.bool,
  onBlur: PropTypes.func,
  onOpen: PropTypes.func,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onFocus: PropTypes.func,
  onSubmit: PropTypes.func,
  optionsMenu: PropTypes.node,
  saveInProgress: PropTypes.bool,
  saveError: PropTypes.string,
  showAsEditButton: PropTypes.bool,
};

QuestionForm.defaultProps = {
  focused: false,
  hasOwnQuestions: false,
  question: '',
  commentId: '',
  isOpen: false,
  onBlur: () => {},
  onChange: () => {},
  onClose: () => {},
  onFocus: () => {},
  onOpen: () => {},
  onSubmit: () => {},
  optionsMenu: null,
  saveInProgress: false,
  saveError: null,
  showAsEditButton: false,
};

export default React.memo(QuestionForm);
